import { checkSettingsStore } from '@/services/SettingsStoreUpdateService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  buildExtraPurchaseFieldBindings,
  parseExtraPurchaseFieldBindings,
} from './extraPurchaseFieldBindings'
import { getEffectiveExtraFields } from './fieldGroups'
import type { ICustomField } from './modals/CustomExtraFieldModal/types'

export const EXTRA_PURCHASE_FIELDS = [
  { key: 'studentName', label: 'ФИО ученика', destination: 'Имя ученика' },
  {
    key: 'studentEmail',
    label: 'Почта ученика',
    destination: 'Почта учащегося',
  },
  {
    key: 'studentPhone',
    label: 'Номер телефона учащегося',
    destination: 'Телефон учащегося',
  },
  { key: 'password', label: 'Пароль ученика', destination: 'Пароль ученика' },
  { key: 'parentName', label: 'ФИО родителя', destination: 'ФИО родителя' },
  {
    key: 'parentPhone',
    label: 'Номер родителя',
    destination: 'Телефон родителя',
  },
  {
    key: 'parentEmail',
    label: 'Почта родителя',
    destination: 'Почта родителя',
  },
  {
    key: 'parentPassword',
    label: 'Пароль родителя',
    destination: 'Пароль родителя',
  },
]

const removeDeprecatedFields = (fields: string[]): string[] =>
  fields.filter((field) => field !== 'customText')

const FIELD_ORDER = EXTRA_PURCHASE_FIELDS.map((f) => f.key)

interface ExtraFieldRule {
  key: string
  state: boolean
  tooltip: string
}

export const useGetExtraPurchaseFields = (): {
  extraFields: string[]
  storedExtraFields: string[]
  setFields: React.Dispatch<React.SetStateAction<string[]>>
  toggleField: (fieldKey: string) => string[]
  computeToggledFields: (currentFields: string[], fieldKey: string) => string[]
  fieldsRules: ExtraFieldRule[]
} => {
  const isSmsIntegration = useSelector(
    (store: any) =>
      store.settings?.sms_integration_enabled === '1' &&
      (store.settings?.confirmationByPhoneNumber === '1' ||
        store.settings?.confirmationByTelegram === '1'),
  )

  const requiredNumberConfirmation = useSelector(
    (store: any) => store.settings?.requiredNumberConfirmationForCatalog,
  )

  const filterExtraFieldsWithSMSIntegration = (fields: string[]): string[] => {
    let updated = fields
    if (isSmsIntegration && updated.includes('studentPhone')) {
      if (requiredNumberConfirmation == 1) {
        updated = updated.filter((item) => item !== 'password')
      } else {
        updated = [...new Set([...updated, 'password'])]
      }
    }
    return updated
  }

  const { customFields, storedBindingsRaw } = useSelector((store: any) => {
    let fields: ICustomField[] = []

    try {
      fields = JSON.parse(store.settings?.extraPurchaseCustomFields ?? '[]')
    } catch (error) {
      console.error(error)
    }

    return {
      customFields: fields,
      storedBindingsRaw: store.settings?.extraPurchaseFieldBindings ?? '[]',
    }
  })

  const bindingsByKey = useMemo(() => {
    const bindings = buildExtraPurchaseFieldBindings({
      storedBindings: parseExtraPurchaseFieldBindings(storedBindingsRaw),
      customFields,
    })

    return new Map(bindings.map((binding) => [binding.key, binding] as const))
  }, [customFields, storedBindingsRaw])

  const extraFieldsStore = useSelector((store: any) => {
    try {
      checkSettingsStore()
      let stored = removeDeprecatedFields(
        JSON.parse(store.settings?.extraPurchaseFields ?? '[]') as string[],
      )
      let updated = removeDeprecatedFields(
        JSON.parse(store.settings?.extraPurchaseFields ?? '[]') as string[],
      )

      updated = filterExtraFieldsWithSMSIntegration(updated)

      // Сортировка по порядку из EXTRA_PURCHASE_FIELDS
      // updated = updated.sort((a, b) => {
      //   return FIELD_ORDER.indexOf(a) - FIELD_ORDER.indexOf(b)
      // })
      updated = updated.sort((a, b) => {
        return stored.indexOf(a) - stored.indexOf(b)
      })

      return updated
    } catch {
      return ['studentEmail']
    }
  })

  const [fields, setFields] = useState<string[]>(extraFieldsStore)

  const fieldsRules = useMemo(() => {
    let rules: ExtraFieldRule[] = []
    if (isSmsIntegration && fields.includes('studentPhone')) {
      rules = [
        ...rules,
        {
          key: 'password',
          state: requiredNumberConfirmation == 1 ? false : true,
          tooltip:
            requiredNumberConfirmation == 1
              ? 'Скрыто из-за настроек SMS-интеграции'
              : 'Включено из-за настроек SMS-интеграции',
        },
      ]
    }
    return rules
  }, [fields, isSmsIntegration, requiredNumberConfirmation])

  const effectiveExtraFields = useMemo(
    () => getEffectiveExtraFields(fields, bindingsByKey, fieldsRules),
    [bindingsByKey, fields, fieldsRules],
  )

  useEffect(() => {
    checkSettingsStore()
    setFields(extraFieldsStore)
  }, [extraFieldsStore.length, isSmsIntegration])

  const computeToggledFields = useCallback(
    (currentFields: string[], fieldKey: string): string[] => {
      let updated = [...currentFields]

      if (currentFields.includes(fieldKey)) {
        updated = updated.filter((item) => item !== fieldKey)
      } else {
        updated = [...updated, fieldKey]
      }

      updated = filterExtraFieldsWithSMSIntegration(updated)

      return updated
    },
    [isSmsIntegration, requiredNumberConfirmation],
  )

  const toggleField = (fieldKey: string): string[] => {
    const nextFields = computeToggledFields(fields, fieldKey)
    setFields(nextFields)
    return nextFields
  }

  return {
    extraFields: effectiveExtraFields,
    storedExtraFields: fields,
    setFields: setFields,
    toggleField,
    computeToggledFields,
    fieldsRules,
  }
}
